import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { RobotHead } from 'robot-heads'
import { Button } from '@/components/ui/Button'
import GradientASCII from '@/components/ui/GradientASCII'
import { staggerContainer } from '@/lib/animations'
import { identity } from '@/content/portfolio'

export function CVHero() {
  const [reducedMotion, setReducedMotion] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReducedMotion(preference.matches)
    update()
    preference.addEventListener('change', update)
    return () => preference.removeEventListener('change', update)
  }, [])

  return (
    <section
      className="relative flex items-center pt-20 overflow-hidden"
    >
      <div className="hero-ascii-field" aria-hidden="true">
        <GradientASCII
          colors={['#702b25', '#f34e3f', '#f8994f', '#dfdfc1']}
          shape="horizon"
          cellSize={18}
          density={0.8}
          glyphOpacity={0.85}
          gradientOpacity={0.55}
          glow={0.2}
          speed={0.3}
          scale={1}
          flicker={0.1}
          grain={0.02}
          mouseInteraction={false}
          clickEffect="none"
          dpr={1.5}
        />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-16">
        <motion.div
          className="relative grid lg:grid-cols-2 gap-10 lg:gap-16 items-center"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          {/* Left: Identity + Contact */}
          <div className="min-w-0">
            <div className="section-label text-accent-primary min-h-20 pr-24 lg:min-h-0 lg:pr-0">
              {identity.fullName} · Product / Engineering / AI
            </div>

            <h1 className="mt-5 text-4xl md:text-5xl font-medium tracking-tighter text-text-heading leading-[1.1]">
              Senior Technical Product Manager &amp;{' '}
              <span className="text-accent-primary"><span className="whitespace-nowrap">AI-native</span> Product Builder</span>
            </h1>

            <p className="mt-6 text-lg lg:text-xl text-text-primary max-w-2xl leading-relaxed">
              I turn complex workflows into products people can use. Drawing on a software engineering background,
              I work across discovery, prototyping, and delivery for public products, internal platforms, and AI workflows.
            </p>

            <p className="mt-5 text-sm lg:text-base text-text-secondary">{identity.location}</p>

            {/* CTAs */}
            <div className="mt-10 flex flex-wrap gap-4">
              <Button
                variant="primary"
                size="large"
                href="/work"
                showArrow
              >
                Explore my work
              </Button>
              <Button
                variant="outline"
                size="large"
                href="#connect"
              >
                Get in touch
              </Button>
            </div>
          </div>

          {/* Decorative companion: beside the eyebrow on small screens, right of the hero on desktop. */}
          <div
            className="pointer-events-none absolute right-0 top-0 h-20 w-20 lg:left-3/4 lg:top-1/2 lg:h-60 lg:w-60 lg:-translate-x-1/2 lg:-translate-y-1/2"
            aria-hidden="true"
          >
            <RobotHead
              shape="rectangle"
              state="idle"
              size={240}
              color="#f34e3f"
              trimColor="#242826"
              screenColor="#dfdfc1"
              speed={0.5}
              paused={reducedMotion}
              className="origin-top-left scale-[0.333333] lg:scale-100"
            />
          </div>
        </motion.div>
      </div>
    </section>
  )
}
